import axios from 'axios';
import {cloneDeep} from 'lodash';
import moment from 'moment-timezone';
import PQueue from 'p-queue';
import pdfMake from 'src/modules/shared/pdf/pdfMakeClient';
import Resizer from 'react-image-file-resizer';
import {i18n} from 'src/i18n';
import {formatIncidentDateAnswerIfBlumar} from 'src/modules/incident/incidentDateWeekday';
import {isIncidentYesNoKeyPointer} from 'src/modules/incident/incidentFormAnswerCoerce';
import SettingsService from 'src/modules/settings/settingsService';
import {
  buildDynamicSubsectionGroupKey,
  countDynamicSubsectionHeaders,
  findDynamicSubsectionHeaderForItem,
  formatDynamicSubsectionColumnLabelForPdf,
  getDynamicSubsectionColumnItems,
  isItemInDynamicSubsectionForPdf,
  isPlastyvergTenant,
} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {
  findMultipleChoiceTreeFullPath,
  getMultipleChoiceTreeDisplayLabel,
} from 'src/modules/shared/alternativeTreeDisplay';
import {createAncestorNaTracker, isContainerNotApplicable} from 'src/modules/tarea/tareaParcialHelpers';
import TareaService from 'src/modules/tarea/tareaService';
import {getTaskVisualLabel} from 'src/modules/tarea/taskVisualLabel';
import TenantService from 'src/modules/tenant/tenantService';
import UserService from 'src/modules/user/userService';
import {
  getTenantPdfFeatureKeysFromStore,
  pdfMultiCheckboxSelectedOnlyEnabled,
  pdfPhotoDateStampEnabled,
  pdfQualityScoreColumnsEnabled,
} from 'src/security/taskPdfFeatureGates';

/** Flag para usar el tareaPdfService del backend en lugar del frontend. Útil para pruebas. */
export const USE_BACKEND_FOR_PDF = false; //

const textOverImage = (imgSrc, text) => {
  return new Promise((resolve, reject) => {
    // Crear un elemento de imagen en el DOM
    const img = new Image();
    img.src = imgSrc;

    img.onload = function () {
      // Crear un lienzo (canvas) en memoria
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      // Obtener contexto 2D del canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.log('PDF canvas error', 'No access to 2d context');
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0); // Dibujar la imagen original en el canvas

      // Fecha que quieres sobreponer (puedes personalizar el formato)
      const textOptions: any = {
        text,
        fontSize: '26px',
        fontColor: 'white',
        fontFamily: 'Arial',
        textAlign: 'left',
        textBaseline: 'bottom',
      };

      // Sobreponer texto en la imagen en el canvas
      ctx.fillStyle = textOptions.fontColor;
      ctx.font = textOptions.fontSize + ' ' + textOptions.fontFamily;
      ctx.textAlign = textOptions.textAlign;
      ctx.textBaseline = textOptions.textBaseline;
      ctx.fillText(textOptions.text, canvas.width - 222, canvas.height - 15); // Posiciona el texto centrado en la parte inferior

      // Convertir el canvas a una imagen base64
      const modifiedImageBase64 = canvas.toDataURL('image/jpeg'); // Puedes cambiar 'image/jpeg' según el tipo de imagen que necesites

      // Resolver la promesa con la imagen modificada
      resolve(modifiedImageBase64);
    };

    img.onerror = function (err) {
      // Rechazar la promesa en caso de error
      reject(err);
    };
  });
};

const withWhiteBackground = (imgSrc) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imgSrc;
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No access to 2d context'));
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = reject;
  });
};

const resizeFile = (file, resize) => {
  console.log('%c⧭', 'color: #86bf60', 'RESIZING!');
  if (!resize) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Error reading blob as data URL'));
      reader.readAsDataURL(file);
    });
  }
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      600, // Ancho máximo
      600, // Alto máximo
      'JPEG',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64',
    );
  });
};

const urlToBase64 = async (url, {isBlumarPT = false, imgText, resize = true}: any = {}) => {
  try {
    const cacheBuster = `v=${Math.floor(Math.random() * 10) + 1}`;
    const urlWithCacheBuster = String(url).includes('?') ? `${url}&${cacheBuster}` : `${url}?${cacheBuster}`;
    const response = await axios.get(urlWithCacheBuster, {responseType: 'blob'});
    const resizedImg = await resizeFile(response.data, resize);
    if (isBlumarPT) {
      const imgWithDate = await textOverImage(resizedImg, imgText);
      return imgWithDate;
    }
    return resizedImg;
  } catch (error) {
    console.log('urlToBase64 error', {error});
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';
  }
};

const isDataImageUrl = (value) => {
  return typeof value === 'string' && value.startsWith('data:image');
};

const resolveProcedureSignatureImage = async (signature, signatureRef) => {
  const signatureSource = signatureRef?.downloadUrl || signatureRef?.publicUrl || signature;
  if (!signatureSource) return null;
  if (isDataImageUrl(signatureSource)) {
    try {
      return await withWhiteBackground(signatureSource);
    } catch (error) {
      return signatureSource;
    }
  }
  if (typeof signatureSource === 'string' && /^https?:\/\//i.test(signatureSource)) {
    const signatureImage = await urlToBase64(signatureSource, {resize: false});
    try {
      return await withWhiteBackground(signatureImage);
    } catch (error) {
      return signatureImage;
    }
  }
  return signatureSource;
};

const getTextMaxWidth = (content) => {
  const pdf = pdfMake.createPdf({content});
  pdf.getStream({});
  return {
    ...pdf.docDefinition.content[0],
    width: pdf.docDefinition.content[0]._maxWidth,
  };
};

/** Respuestas tipo hora: a veces vienen como cadena "HH:mm" / "H:mm" / con segundos sin fecha ISO. */
const formatProcedureTimeForPdf = (value: any): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      const parsed = moment(trimmed, ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm'], true);
      if (parsed.isValid()) return parsed.format('HH:mm');
    }
  }
  const m = moment(value);
  return m.isValid() ? m.format('HH:mm') : typeof value === 'string' ? value : '';
};

const tareaPdf = {
  generatePdfContent: async (
    tarea,
    {tenant = null, settings = null, open = true, pageOrientation, featureKeys: featureKeysFromArgs}: any = {},
  ) => {
    if (tarea.id !== 'consolidatedTask') {
      tarea = await TareaService.find(tarea.id);
    }
    tenant = tenant || (await TenantService.findCurrent());
    const tenantName = tenant.name ?? '';
    const isServiOctava = tenantName === 'SERVIOCTAVA';
    const imgsPerRowConsolidated = !isServiOctava ? 2 : 4;
    const imgFitConsolidated = !isServiOctava ? [258, 310] : [120, 318];
    const imgsPerRow = !isServiOctava ? 2 : 4;
    const imgFit = !isServiOctava ? [250, 300] : [120, 300];
    const columnGap = !isServiOctava ? undefined : 2;
    const resolvedFeatureKeys = featureKeysFromArgs ?? getTenantPdfFeatureKeysFromStore();
    const pdfQualityScoreColumns = pdfQualityScoreColumnsEnabled(tenant?.id, resolvedFeatureKeys);
    const pdfPhotoDateStamp = pdfPhotoDateStampEnabled(tenant?.id, resolvedFeatureKeys);
    const pdfMultiCheckboxSelectedOnly = pdfMultiCheckboxSelectedOnlyEnabled(tenant?.id, resolvedFeatureKeys);
    const text = (text, styles = {}) => {
      let textLabel = 'text';
      // check if((textis array or textis object)) { textLabel = "stack"; }
      if (Array.isArray(text) || typeof text === 'object') {
        textLabel = 'stack';
      }

      return {[textLabel]: text, fontSize: 9, margin: [0, textMargin, 0, textMargin + 1], alignment: 'left', ...styles};
    };
    const imgs = async (images, styles = {}, fit = imgFit, resize = true) => {
      //console.log('%c⧭ imgs', 'color: #408059', images);
      const imgs: any = [];
      const atLeastOneAIContent = images.some((img) => img?.AIContent);
      const queue = new PQueue({concurrency: 1});
      await queue.addAll(
        images.map((img, index) => async () => {
          const imgText = img.file?.datetime ? moment(img.file?.datetime).format('DD/MM/YYYY HH:mm') : '';
          if (isConsolidated) {
            imgs.push({
              stack: [
                {text: img.titulo, fontSize: 9},
                {image: await urlToBase64(img.publicUrl, {isBlumarPT: pdfPhotoDateStamp, imgText, resize}), fit: fit, ...styles},
              ],
            });
          } else {
            imgs.push({
              image: await urlToBase64(img.publicUrl, {isBlumarPT: pdfPhotoDateStamp, imgText, resize}),
              fit: fit,
              ...styles,
            });
          }
          const AIContent = img?.AIContent;
          if (atLeastOneAIContent) {
            let imageDescriptions: any = [[{}, {}]];
            if (AIContent) {
              imageDescriptions = Object.keys(AIContent).map((key) => {
                let content: any = AIContent[key];
                if (Array.isArray(content)) {
                  content = {
                    stack: content.map((item) => {
                      return {text: item, fontSize: 8, alignment: 'left'};
                    }),
                    margin: [1, 1],
                  };
                } else {
                  content = {text: content, fontSize: 8, margin: [1, 1], alignment: 'left'};
                }
                return [
                  {
                    text: key,
                    fontSize: 8,
                    bold: true,
                    margin: [1, 1],
                    alignment: 'left',
                  },
                  content,
                ];
              });
              imgs.push(
                infoTable(
                  {
                    widths: ['auto', '*'],
                    body: imageDescriptions,
                  },
                  {margin: [0, isConsolidated ? 12 : 0, 0, 0]},
                ),
              );
            } else {
              const imgsLength = imgs.length;
              const nextSlotIsPair = imgsLength % 2 === 0;
              //const lastImg = index === 0 ? null : images[index - 1];
              const nextImg = index === images.length - 1 ? null : images[index + 1];
              if (nextImg?.AIContent && !nextSlotIsPair) {
                imgs.push({text: '', width: 250});
              }
            }
          }
        }),
      );
      return imgs;
    };
    const breakArray = (array, size) => {
      const result: any = [];
      for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
      }
      return result;
    };
    const imgsGrid = async (images, styles: any = {}, size = imgsPerRow, imgStyles: any = {}) => {
      const booleanFilteredImages = images?.filter(Boolean) || [];
      const fit = imgStyles?.fit;
      const resize = 'resize' in imgStyles ? imgStyles.resize : true;
      return breakArray(await imgs(booleanFilteredImages, imgStyles, fit, resize), size).map((columns) => ({
        columns,
        ...styles,
        unbreakable: true,
      }));
    };
    const isNumeric = (val) => {
      if (typeof val === 'string') {
        val = val.replace(/,/g, '.'); // Reemplazar comas por puntos
      }
      return !isNaN(parseFloat(val)) && isFinite(val);
    };

    // let logo: any = null;
    // if (settings?.logosPdf?.length) {
    //   logo = await urlToBase64(settings.logosPdf[0].publicUrl);
    // } else if (settings?.logos?.length) {
    //   logo = await urlToBase64(settings.logos[0].publicUrl);
    // }
    const minMargin = 20;
    let {
      estado,
      descripcion,
      titulo,
      subtitulo,
      categoria,
      modeloTarea,
      planificacion,
      fechaLimite,
      inicio,
      comienzo,
      fin,
      fechaProgramacion,
      tipoVisualizacion,
      tipoMantenimiento, // Programada
      listaProcedimiento,
      respuestaInterfaz,
      pasoActual,
      firmas,
      documentosEnviados,
      fotosEnviadas,
      dirigidoA,
      ejecutor,
      puesto,
      comentarioEnviado,
      activo,
      horometro,
      kilometraje,
      tituloTarea,
      headers,
      rows,
      summary,
      images,
      isConsolidated,
      parentTask,
    } = tarea;
    const imgText = comienzo ? moment(comienzo).format('DD/MM/YYYY') : '';
    const nombre = planificacion?.nombre;
    firmas &&
      (await Promise.all(
        firmas.map(async (f) => {
          if (!f.firma || f.responsable?.id) return;
          const responsable = await UserService.find(f.responsable);
          f.responsable = responsable;
        }),
      ));
    //console.log('%c⧭ firmas', 'color: #e57373', firmas);

    const tituloPdf = `${titulo} ${tituloTarea || modeloTarea?.titulo || descripcion}`; // (${estado})`;
    const descripcionTarea = modeloTarea?.descripcion;
    const imagenesTarea = modeloTarea?.fotosAdjuntas;
    const fechaIni = comienzo ? moment(comienzo).format('DD-MM-YYYY HH:mm') : null;
    //fechaProgramacion ? moment(fechaProgramacion).format('DD MMMM YYYY') : null;
    const fechaVencimiento = fechaLimite ? moment(fechaLimite).format('DD MMMM YYYY') : null;
    const fechaFin = fin ? moment(fin).format('DD-MM-YYYY HH:mm') : null;
    const elDirigido = ejecutor ? ejecutor.fullName || ejecutor.email : puesto?.nombre;

    const content: any = [];
    const textMargin = 0;
    const replyMargin = [10, textMargin + 1, 0, 0];
    const dynamicReplyMargin = [0, 0, 0, 0];
    const dynamicFontSize = pdfQualityScoreColumns ? 6.5 : 7.5;
    // const tituloTable = {
    //   layout: 'headerTable',
    //   width: '*',
    //   alignment: 'center',
    //   table: {
    //     widths: ['auto', '*', 'auto'],
    //     body: [
    //       [
    //         logo
    //           ? {minWidth: 80, maxWidth: 100, image: logo, maxHeight: 40, rowSpan: 2, margin: [0, 5]}
    //           : {text: tenantName, rowSpan: 2, margin: [0, 5]},
    //         {
    //           text: [{text: tituloPdf, fontSize: 13, bold: true}, subtitulo ? {text: '\n' + subtitulo, fontSize: 10} : {}],
    //           margin: [8, 5],
    //           rowSpan: 2,
    //         },
    //         fechaIni
    //           ? {
    //               text: [
    //                 {text: isConsolidated ? 'Desde' : 'Desde', bold: true, noWrap: true, fontSize: 9},
    //                 {text: '\n'},
    //                 {text: fechaIni || '', noWrap: true, fontSize: 10},
    //               ],
    //               margin: [1, 3],
    //               alignment: 'left',
    //             }
    //           : {},
    //       ],
    //       [
    //         {},
    //         {},
    //         fechaFin
    //           ? {
    //               text: [
    //                 {text: isConsolidated ? 'Hasta' : 'Hasta', bold: true, noWrap: true, fontSize: 9},
    //                 {text: '\n'},
    //                 {text: fechaFin || '', noWrap: true, fontSize: 10},
    //               ],
    //               margin: [1, 3],
    //               alignment: 'left',
    //             }
    //           : {},
    //         //fechaVencimiento ? { text: [{text: 'Vencimiento: ', fontSize: 10, bold: true}, fechaVencimiento], alignment: 'right' } : ''
    //       ],
    //     ],
    //   },
    // };
    //content.push(tituloTable);
    //content.push(text(tituloPdf, { alignment: 'center', fontSize: 13, bold: true }));
    //if (subtitulo) content.push(text(subtitulo, { alignment: 'center', fontSize: 9 }));
    //content.push({text:'', margin: [0, 16]});
    // content.push({
    //   columns: [
    //     { text: nombre? 'Planificación: ' + nombre: 'No planificado/No programado', alignment: 'left' },
    //     { text: activo ? `Activo: ${activo.nombre}` : '', alignment: 'right' },
    //   ], margin: [0, 5]
    // });
    //const codigo = activo?.codigo || activo?.patente; // en pesqueras pusieron la patente en el nombre
    const infoTable = (table, extra: any = {}) => ({
      layout: 'table',
      width: '*',
      alignment: 'center',
      table,
      ...extra,
    });

    if (activo) {
      //const activoLabel = `${activo?.nombre}${activo?.codigo ? ` | ${activo.codigo}` : ''}`;
      // content.push({
      //   text: [activo ? {text: `Activo: `, bold: true} : '', activo ? {text: activoLabel, bold: false} : ''],
      //   margin: [0, 0, 0, 0],
      //   alignment: 'center',
      //   fontSize: 13,
      // });
      const activoTableBody: any = [];
      if (activo?.nombre && activo?.nombre !== activo?.codigo) {
        activoTableBody.push([
          {
            text: i18n('entities.activo.fields.nombre'),
            bold: true,
            fontSize: 10,
            alignment: 'left',
          },
          {text: activo.nombre, fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      if (activo?.codigo) {
        activoTableBody.push([
          {text: i18n('entities.activo.fields.codigo'), bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.codigo, fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      if (activo?.patente) {
        activoTableBody.push([
          {text: i18n('entities.activo.fields.patente'), bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.patente, fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      if (activo?.tipoActivo?.length > 0) {
        activoTableBody.push([
          {text: i18n('entities.activo.fields.tipoActivo'), bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.tipoActivo.map((t) => t.descripcion).join(', '), fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      if (activo?.marca) {
        activoTableBody.push([
          {text: i18n('entities.activo.fields.marca'), bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.marca, fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      if (activo?.modelo) {
        activoTableBody.push([
          {text: i18n('entities.activo.fields.modelo'), bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.modelo, fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      if (activo?.numeroSerial) {
        activoTableBody.push([
          {text: i18n('entities.activo.fields.numeroSerial'), bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.numeroSerial, fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      // if (activo?.localizacion) {
      //   activoTableBody.push([
      //     {text: i18n('entities.activo.fields.localizacion'), bold: true, fontSize: 10, alignment: 'left'},
      //     {text: activo.localizacion.label, fontSize: 10, alignment: 'left'},
      //     {},
      //   ]);
      // }
      if (activo?.locations) {
        activoTableBody.push([
          //i18n('common.locations')
          {text: 'Ubicación', bold: true, fontSize: 10, alignment: 'left'},
          {text: activo.locations.map((l) => l.label).join(', '), fontSize: 10, alignment: 'left'},
          {},
        ]);
      }
      const fotos = activo.fotos;
      const activoFotos = fotos?.length > 0 ? await imgsGrid(fotos, {margin: [0, 0, 0, 0]}, 1, {fit: [100, 100]}) : null;
      activoTableBody.forEach((row, index) => {
        const secondColumn = row[1];
        const thirdColumn = row[2];
        if (activoFotos?.length > 0) {
          if (index === 0) {
            thirdColumn.stack = activoFotos;
            thirdColumn.rowSpan = activoTableBody.length;
          }
        } else {
          secondColumn.colSpan = 2;
        }
      });
      const activoTable = infoTable(
        {
          headerRows: 1,
          widths: [80, '*', 'auto'],
          body: [
            [{text: 'Activo', bold: true, fontSize: 10, alignment: 'center', colSpan: 3, margin: [0, 1]}, {}, {}],
            ...activoTableBody,
          ],
        },
        {
          margin: [0, 10, 0, 20],
        },
      );
      content.push(activoTable);
    }
    const firmasConNota = firmas?.filter((f) => {
      return f.nota && f.firmada;
    });
    if (firmasConNota?.length > 0) {
      // console.log('%c⧭ firmasConNota', 'color: #006dcc', firmasConNota);
      const firmasConNotaTable = infoTable(
        {
          headerRows: 1,
          widths: ['auto', 'auto', '*'],
          body: [
            [{text: 'Notas', bold: true, fontSize: 10, alignment: 'center', colSpan: 3, margin: [0, 1]}, {}, {}],
            ...firmasConNota.map((f) => [
              {text: f.updatedAt ? moment(f.updatedAt).format('DD/MM/YYYY HH:mm') : '', fontSize: 10, alignment: 'left'},
              {text: f.responsable?.fullName || f.responsable?.email, fontSize: 10, alignment: 'left'},
              {text: f.nota, fontSize: 10, alignment: 'left'},
            ]),
          ],
        },
        {
          margin: [0, 10, 0, 20],
        },
      );
      content.push(firmasConNotaTable);
    }

    /* {tipoVisualizacion === 'Procedimiento' && <p>Tipo: Procedimiento</p>} */
    const infoBody: any = [];
    if (nombre) {
      infoBody.push([
        {text: 'Planificación', bold: true, fontSize: 10, alignment: 'left'},
        {text: nombre || 'No planificado', fontSize: 10, alignment: 'left'},
      ]);
    }
    // Categoría oculta en tabla info general
    // if (!isServiOctava && categoria) {
    //   infoBody.push([
    //     {text: 'Categoría', bold: true, fontSize: 10, alignment: 'left'},
    //     {text: categoria.nombre, fontSize: 10, alignment: 'left'},
    //   ]);
    // }
    if (!isServiOctava && elDirigido) {
      infoBody.push([
        {text: 'Ejecutor', bold: true, fontSize: 10, alignment: 'left'},
        {text: elDirigido, fontSize: 10, alignment: 'left'},
      ]);
    }
    //content.push(text(`Realizado por: ${elDirigido}`));
    if (horometro)
      infoBody.push([
        {text: 'Horómetro', bold: true, fontSize: 10, alignment: 'left'},
        {text: horometro, fontSize: 10, alignment: 'left'},
      ]);
    if (kilometraje)
      infoBody.push([
        {text: 'Kilometraje', bold: true, fontSize: 10, alignment: 'left'},
        {text: kilometraje, fontSize: 10, alignment: 'left'},
      ]);
    //if (horometro) content.push(text('Horómetro: ' + horometro));
    if (descripcionTarea)
      infoBody.push([
        {text: 'Descripción', bold: true, fontSize: 10, alignment: 'left'},
        {text: descripcionTarea, fontSize: 10, alignment: 'left'},
      ]);
    // console.log('%c⧭ parentTask', 'color: #f2ceb6', parentTask);
    if (parentTask)
      infoBody.push([
        {text: 'Tarea Origen', bold: true, fontSize: 10, alignment: 'left'},
        {
          text: `${getTaskVisualLabel(parentTask) || parentTask.titulo || ''}${parentTask.tituloTarea ? ` ${parentTask.tituloTarea}` : ''}`,
          fontSize: 10,
          alignment: 'left',
        }
      ]);
    //if (descripcionTarea) content.push(text('Descripción: ' + descripcionTarea));

    if (!isConsolidated && infoBody.length > 0) {
      content.push(
        infoTable(
          {
            widths: [80, '*'],
            body: infoBody,
          },
          {
            margin: [0, activo ? 0 : 10, 0, 8],
          },
        ),
      );
    }

    if (isConsolidated) {
      content.push({
        text: 'DATOS DE LA OPERACIÓN',
        bold: true,
        fontSize: 12,
        alignment: 'left',
        margin: [0, 10, 0, 4],
      });
      const summaryTable = {
        headerRows: 1,
        widths: ['auto', '*'],
        body: [
          ...summary
            .filter((s) => s.showInSummary)
            .map((s) => [
              {text: s.label, alignment: 'left'},
              {text: s.value || '', alignment: 'left'},
            ]),
        ],
      };
      content.push(
        infoTable(summaryTable, {
          margin: [0, 0, 0, 0],
        }),
      );
      if (headers && rows) {
        content.push({
          text: 'TABLA DE CUADRATURA',
          bold: true,
          fontSize: 12,
          alignment: 'left',
          margin: [0, 10, 0, 4],
          pageBreak: 'before',
          pageOrientation: 'landscape',
        });
        const headersInTable = headers.filter((h) => h.showInTable);
        // split headers in groups of 10
        const headersGroups = breakArray(headersInTable, 8);
        //console.log('%c⧭ headersGroups', 'color: #ff0000', headersGroups);
        const rowsGrouped = rows.map((row) => {
          const rowsInTable = row.filter((cell) => cell.showInTable);
          return breakArray(rowsInTable, 8);
        });
        for (let i = 0; i < headersGroups.length; i++) {
          const headerGroup = headersGroups[i];
          //console.log('%c⧭ headerGroup', 'color: #ff0000', headerGroup);
          const table = {
            headerRows: 1,
            widths: headerGroup.map(() => 'auto'),
            heights: 20,
            body: [
              headerGroup.map((header) => ({text: header.label, fontSize: 10, alignment: 'left', bold: true})),
              ...rowsGrouped.map((row) =>
                row[i].map((cell, index) => {
                  return {
                    columns: [{text: cell.value || '', alignment: 'left'}],
                    minWidth: 20,
                  };
                }),
              ),
            ],
          };
          const pageBreak = i > 0 ? 'before' : undefined;
          const newInfoTable = infoTable(table, {
            margin: [0, i > 0 ? 20 : 0, 0, 0],
            pageBreak,
          });
          //console.log('i:', getTextMaxWidth([newInfoTable]));
          content.push(newInfoTable);
        }
        // const consolidatedTable = {
        //   headerRows: 2,
        //   //dontBreakRows: true,
        //   widths: [...headersInTable.map(() => 'auto')],
        //   body: [
        //     headersInTable.map((header) => ({text: header.label, fontSize: 10, alignment: 'left', bold: true})),
        //     ...rows.map((row) =>
        //       row
        //         .filter((cell) => cell.showInTable)
        //         .map((cell, index) => ({text: cell.value || '', alignment: 'left', bold: index === 0})),
        //     ),
        //   ],
        // };
        // console.log('%c⧭ consolidatedTable', 'color: #f2ceb6', consolidatedTable);
        // content.push(
        //   infoTable(consolidatedTable, {
        //     margin: [0, 0, 0, 0],
        //   }),
        // );
      }
      if (images?.length > 0) {
        content.push({
          text: 'FOTOS ADJUNTAS',
          bold: true,
          fontSize: 12,
          alignment: 'left',
          margin: [0, 0, 0, 4],
          pageBreak: 'before',
          pageOrientation: 'portrait',
        });
        content.push(...(await imgsGrid(images, {margin: [0, 0, 0, 10]}, imgsPerRowConsolidated, {fit: imgFitConsolidated})));
      }
    }

    // armar una tabla con las tareas hijas de la tarea actual
    const tareaWithChildren = await TareaService.list({parentTask: tarea.id}, 'createdAt_DESC', 50, null);
    console.log('%c⧭ tareaWithChildren', 'color: #f2ceb6', tareaWithChildren);

    if (tareaWithChildren?.rows?.length > 0) {
      const tareasHijas = tareaWithChildren.rows;
      if (tareasHijas && tareasHijas.length > 0) {
        const tareasHijasTable = infoTable(
          {
            headerRows: 2,
            widths: ['auto', 200, 'auto', 'auto'],
            body: [
              [
                {text: 'Tareas Relacionadas', bold: true, fontSize: 10, alignment: 'center', colSpan: 4, margin: [0, 1]},
                // {},
                {},
                {},
                {},
              ],
              [
                {text: 'Tarea', bold: true, fontSize: 10},
                // {text: 'Estado', bold: true, fontSize: 10},
                {text: 'Ejecutor', bold: true, fontSize: 10},
                {text: 'Inicio', bold: true, fontSize: 10},
                {text: 'Fin', bold: true, fontSize: 10},
              ],
              ...tareasHijas.map((tareaHija) => [
                {
                  text:
                        (getTaskVisualLabel(tareaHija) || tareaHija.titulo || '') +
                        (tareaHija.tituloTarea ? ` ${tareaHija.tituloTarea}` : ''),
                  fontSize: 10,
                  alignment: 'left',
                },
                // {text: tareaHija.estado, fontSize: 10, alignment: 'left'},
                {text: tareaHija.ejecutor?.fullName || '', fontSize: 10, alignment: 'left'},
                {text: tareaHija.comienzo ? moment(tareaHija.comienzo).format('DD-MM-YYYY') : 'Pendiente', fontSize: 10},
                {text: tareaHija.fin ? moment(tareaHija.fin).format('DD-MM-YYYY') : 'Pendiente', fontSize: 10},
              ]),
            ],
          },
          {
            margin: [0, infoBody.length > 0 ? 12 : 20, 0, 8],
          },
        );
        content.push(tareasHijasTable);
      }
    }

    const queue = new PQueue({concurrency: 1});
    // let count = 0;
    // queue.on('completed', (result) => {
    //   console.log(`Completed item #${++count}.`, result);
    // });
    if (['Checklist', 'Pasos'].includes(tipoVisualizacion) && respuestaInterfaz && modeloTarea?.subTareasRelacionadas?.length) {
      const subTareasRelacionadas = modeloTarea.subTareasRelacionadas;
      const totalSubTareas = subTareasRelacionadas.length;
      const respuestaFilter = tipoVisualizacion === 'Checklist' ? (r, index) => r?.checkbox : (r, index) => index < pasoActual;
      const marginCheck = tipoVisualizacion === 'Checklist' ? [0, 0, 0, -5] : [0, 0, 0, -6];
      let subTareasCompletadas = respuestaInterfaz.filter(respuestaFilter).length;
      if (subTareasCompletadas > totalSubTareas) subTareasCompletadas = totalSubTareas;
      const porcentajeCompletado = (subTareasCompletadas * 100) / totalSubTareas;
      //completado
      const checkPasosBody: any = [];
      checkPasosBody.push([
        {
          text: `Completado: ${porcentajeCompletado.toFixed(1)}%`,
          bold: true,
          fontSize: 10,
          alignment: 'center',
          colSpan: 2,
        },
        {},
      ]);
      //content.push(text(`Completado: ${porcentajeCompletado.toFixed(1)}%`, {alignment: 'center', fontSize: 11, margin: [0, 18]}));
      //listado
      subTareasRelacionadas.forEach((subTarea, index) => {
        const r = respuestaInterfaz[index];
        if (!subTarea) return;
        checkPasosBody.push([
          {
            stack: [
              {
                canvas:
                  tipoVisualizacion === 'Checklist'
                    ? [
                        {
                          type: 'polyline',
                          lineWidth: 1,
                          closePath: true,
                          points: [
                            {x: 0, y: 0},
                            {x: 8, y: 0},
                            {x: 8, y: 8},
                            {x: 0, y: 8},
                          ],
                        },
                      ]
                    : [
                        {
                          type: 'ellipse',
                          x: 4,
                          y: 4,
                          color: 'black',
                          fillOpacity: 1,
                          r1: 5,
                          r2: 5,
                        },
                        {
                          type: 'ellipse',
                          x: 4,
                          y: 4,
                          color: 'white',
                          fillOpacity: 1,
                          r1: 4,
                          r2: 4,
                        },
                      ],
                margin: marginCheck,
              },
              {text: respuestaFilter(r, index) ? '√' : ' ', margin: [0, -5, 0, 0]},
            ],
            width: 8,
            margin: [20, 13, 20, 15],
          },
          {text: subTarea.nombre, fontSize: 11, margin: [40, 11, 20, 15], alignment: 'left'},
        ]);
      });
      content.push(
        infoTable(
          {
            widths: [80, '*'],
            body: checkPasosBody,
          },
          {margin: [0, 25, 0, 0]},
        ),
      );
    }
    //
    let tipoRespuestaOptions = [
      {
        value: 'textField',
        label: 'Campo de Texto',
      },
      {
        value: 'numberField',
        label: 'Campo de Número',
      },
      {
        value: 'multipleChoice',
        label: 'Lista Desplegable',
      },
      {
        value: 'multiCheckbox',
        label: 'Lista de Checkbox',
      },
      {
        value: 'checkbox',
        label: 'Checkbox',
      },
      {
        value: 'inspectionCheck',
        label: 'Inspección',
      },
      {
        value: 'yesNo',
        label: 'Si, No, N/A',
      },
      {
        value: 'ammount',
        label: 'Monto',
      },
      {
        value: 'registrationNumber',
        label: 'RUT',
      },
      {
        value: 'barcode',
        label: 'Código de Barra',
      },
      {
        value: 'date',
        label: 'Fecha',
      },
      {
        value: 'time',
        label: 'Hora',
      },
      {
        value: 'day',
        label: 'Día',
      },
      {
        value: 'month',
        label: 'Mes',
      },
      {
        value: 'year',
        label: 'Año',
      },
      {
        value: 'location',
        label: 'Ubicación',
      },
      {
        value: 'thirdParty',
        label: 'Tercero',
      },
      {
        value: 'project',
        label: 'Proyecto',
      },
      {
        value: 'asset',
        label: 'Activo',
      },
      {
        value: 'perfilEmpleado',
        label: 'Colaborador',
      },
      {
        value: 'articulo',
        label: 'Artículo',
      },
      {
        value: 'usuario',
        label: 'Usuario',
      },
      {
        value: 'asignadorFuncion',
        label: 'Teams',
      },
      {
        value: 'textFromImage',
        label: 'Texto de Imagen',
      },
      // {
      //   value: 'files',
      //   label: 'Foto / Documento',
      // },
      // {
      //   value: 'signature',
      //   label: 'Firma',
      // },
      {
        value: 'meterReading',
        label: 'Lectura de Medidor',
      },
    ];
    // TODO(customRoles): Layout PDF Camanchaca por tenantId fijo; migrar a featureKeys cuando haya prioridad.
    const isCamanchacaCalidad = tenant.id === '67a60287e5a510a703d35993';
    const isCamanchacaSSO = tenant.id === '68b6208c85a6a0f2b2a70980';
    let horizontalTable = isCamanchacaCalidad;

    /** Valor guardado: id, value, label en objeto, o escalar (p. ej. solo label en árboles sin id). */
    function resolveMultipleChoiceStoredValue(val: any) {
      if (val === null || val === undefined || val === '') return null;
      if (typeof val === 'object' && val !== null) {
        if (val.id != null && val.id !== '') return val.id;
        if (val.value != null && val.value !== '') return val.value;
        if (val.label != null && val.label !== '') return val.label;
      }
      return val;
    }

    function optionMatchesMultipleChoice(opt: any, resolvedVal: any) {
      if (resolvedVal === null || resolvedVal === undefined || resolvedVal === '') return false;
      const id = opt?.id;
      const value = opt?.value;
      const label = opt?.label;
      if (id !== undefined && id !== null && id !== '' && String(id) === String(resolvedVal)) return true;
      if (value !== undefined && value !== null && value !== '' && String(value) === String(resolvedVal)) return true;
      // Alternativas solo con label (sin id): el valor guardado suele ser la etiqueta o coincidir con ella
      if (label !== undefined && label !== null && label !== '' && String(label) === String(resolvedVal)) return true;
      return false;
    }

    /** Ruta "Padre / Hijo / Hoja" como en SelectTreeFormItem (alternativeTree). */
    function findOptionByValue(list, val) {
      if (!list?.length) return null;
      const resolvedVal = resolveMultipleChoiceStoredValue(val);
      if (resolvedVal === null || resolvedVal === undefined || resolvedVal === '') return null;
      for (let opt of list) {
        if (optionMatchesMultipleChoice(opt, resolvedVal)) return opt.label;
        if (opt.children && opt.children.length > 0) {
          const childPath = findOptionByValue(opt.children, resolvedVal);
          if (childPath) {
            const path = opt.label + ' / ' + childPath;
            return path;
          }
        }
      }
      return null;
    }

    function hasTreeAlternatives(list) {
      if (!Array.isArray(list) || !list.length) return false;
      return list.some((opt) => Array.isArray(opt?.children) && opt.children.length > 0);
    }

    if (tipoVisualizacion === 'Procedimiento' && listaProcedimiento) {
      const numberedList: any = [];
      const listMargins = [10, textMargin, 0, textMargin + 1];
      const streetLightMargins = [-15, 2, 0, 0];
      let seccionesContent: any[] = [];
      let subsectionMap: any = new Map();
      let subsectionInfo: any = null;
      const naFlags = listaProcedimiento.map(() => ({skip: false, hide: false, na: false}));
      {
        const tracker = createAncestorNaTracker();
        listaProcedimiento.forEach((item: any, i: number) => {
          tracker.observe(item);
          naFlags[i] = {
            skip: item?.tipo === 'pregunta' && tracker.covered,
            hide: item?.tipo === 'subseccion' && tracker.sectionNa,
            na: isContainerNotApplicable(item),
          };
        });
      }
      await queue.addAll(
        listaProcedimiento.map((elem, lpIndex) => async () => {
          const flags = naFlags[lpIndex];
          if (flags.hide || flags.skip) return;
          const nextItem = listaProcedimiento[lpIndex + 1];
          const nextItemDynamic = nextItem?.isDynamic && (elem.tipo === 'subseccion' || elem.tipo === 'seccion');
          const isDynamic =
            horizontalTable && (isItemInDynamicSubsectionForPdf(listaProcedimiento, lpIndex, elem) || nextItemDynamic);
          if (isDynamic) return;
          // console.log('elem', {elem});
          const {
            tipo,
            additionalPhotos,
            comentario,
            pregunta,
            tipoRespuesta,
            nPregunta,
            alternativas,
            yesNo,
            inspectionCheck,
            multipleChoice,
            textField,
            barcode,
            registrationNumber,
            textFromImage,
            numberField,
            checkbox,
            date,
            signature,
            signatureRef,
            files,
            images,
            time,
            location,
            perfilEmpleado,
            articulo,
            usuario,
            asignadorFuncion,
            thirdParty,
            project,
            asset,
            multiCheckbox,
            meterReading,
            year,
            month,
            day,
            ammount,
            haveCriticality,
            criticality,
            allowOthersOption,
            multipleChoiceOthers,
            multiCheckboxOthers,
            alternativasCheckbox,
          } = elem;
          let headerRows = 1;
          let preguntaContent: any = {
            colSpan: 2,
            table: {
              headerRows: headerRows,
              dontBreakRows: true,
              keepWithHeaderRows: 1,
              widths: [220, 300],
              body: [],
            },
            layout: {
              hLineColor: function (i, node) {
                return 'white';
              },
              hLineWidth: function (i, node) {
                return 1;
              },
              vLineWidth: function (i) {
                return 0;
              },
              paddingTop: function (i, node) {
                if (i === 0) {
                  return 1;
                }
                return 0;
              },
              paddingBottom: function (i, node) {
                return 2;
              },
            },
          };
          if (tipo === 'seccion') {
            if (elem.categoria) {
              const subseccion = listaProcedimiento[lpIndex + 1];
              const seccionMargin = [0, 10, 0, subseccion?.categoria ? 5 : 0];
              seccionesContent.push({text: elem.categoria, alignment: 'center', bold: true, fontSize: 12, margin: seccionMargin});
            }
            if (flags.na) {
              seccionesContent.push({
                text: i18n('common.notApplicable'),
                alignment: 'center',
                fontSize: 10,
                margin: [0, 0, 0, 8],
              });
            }
          } else if (tipo === 'subseccion') {
            // console.log('%c⧭', 'color: #408059', {...subsectionInfo});

            const isDynamic = elem.isDynamic;
            if (subsectionInfo && !isDynamic) {
              subsectionInfo = null;
            }
            if (isDynamic) {
              const groupKey = buildDynamicSubsectionGroupKey(elem);
              subsectionInfo = groupKey ? subsectionMap.get(groupKey) : null;
              if (!subsectionInfo) {
                subsectionInfo = {};
                if (groupKey) {
                  subsectionMap.set(groupKey, subsectionInfo);
                }
                const subsectionItems = getDynamicSubsectionColumnItems(listaProcedimiento, lpIndex);
                subsectionInfo.columnLabels = subsectionItems.map((question) => question.pregunta?.label);
                /**
                 * Para BLUMAR agregar 3 columnas adicionales de calculos
                 */
                if (pdfQualityScoreColumns) {
                  const columnasAdicionales: any = [
                    {pregunta: {label: 'MAYOR'}, tipoRespuesta: 'numberField', isBold: true},
                    {pregunta: {label: 'MENOR'}, tipoRespuesta: 'numberField', isBold: true},
                    {pregunta: {label: 'TOTAL'}, tipoRespuesta: 'numberField', isBold: true},
                    {pregunta: {label: 'AR'}, tipoRespuesta: 'numberField', isBold: true},
                  ];
                  subsectionInfo.columnasAdicionales = columnasAdicionales;
                  subsectionItems.push(...columnasAdicionales);
                }

                subsectionInfo.subsectionItems = subsectionItems;
                subsectionInfo.count = countDynamicSubsectionHeaders(listaProcedimiento, elem);
                subsectionInfo.columnCount = subsectionItems.length;
                subsectionInfo.current = 1;
                subsectionInfo.currentRow = [
                  text(subsectionInfo.current + '.', {bold: true, fontSize: dynamicFontSize + 1, margin: dynamicReplyMargin}),
                ];
                const isPlastyverg = isPlastyvergTenant(tenant.id);
                const pageWidth = pageOrientation === 'landscape' ? 704 : 460;
                const fixedWidth =
                  subsectionItems.length > 14 ? pageWidth / subsectionItems.length : isPlastyverg ? 'auto' : '*';
                subsectionInfo.tableRef = {
                  table: {
                    headerRows: 2,
                    dontBreakRows: false,
                    keepWithHeaderRows: 2,
                    widths: [14, ...subsectionItems.map(() => (pdfQualityScoreColumns ? 'auto' : fixedWidth))],
                    body: [
                      [
                        {
                          text: elem.categoria,
                          alignment: 'center',
                          bold: true,
                          fontSize: 11,
                          colSpan: subsectionInfo.columnCount + 1,
                          borderColor: [null, '#fff'],
                        },
                        ...subsectionItems.map(() => ({text: '', borderColor: [null, '#fff']})),
                      ],
                      [
                        {text: 'N°', bold: true, borderColor: [null, '#000'], fontSize: dynamicFontSize + 1},
                        ...subsectionItems.map((question) => ({
                          text: formatDynamicSubsectionColumnLabelForPdf(question.pregunta?.label, isPlastyverg),
                          borderColor: [null, '#000'],
                          fontSize: question.isBold ? dynamicFontSize : dynamicFontSize - 0.5,
                          bold: !!question.isBold,
                        })),
                      ],
                      subsectionInfo.currentRow,
                    ],
                  },
                  margin: [0, -5, 0, 14],
                  layout: {
                    hLineColor: function (i, node) {
                      return '#E8E8E8';
                    },
                    hLineWidth: function (i, node) {
                      return 1;
                    },
                    vLineWidth: function (i) {
                      return 0;
                    },
                    paddingTop: function (i, node) {
                      return 4;
                    },
                    paddingBottom: function (i, node) {
                      return 3;
                    },
                    paddingLeft: function (i, node) {
                      return 0;
                    },
                    paddingRight: function (i, node) {
                      return 3;
                    },
                  },
                };
                seccionesContent.push(subsectionInfo.tableRef);
              } else {
                subsectionInfo.current++;
                subsectionInfo.currentRow = [
                  text(subsectionInfo.current, {bold: true, fontSize: dynamicFontSize + 1, margin: dynamicReplyMargin}),
                ];
                subsectionInfo.tableRef.table.body.push(subsectionInfo.currentRow);
              }
              // const currentSeccion = seccionesContent[seccionesContent.length - 1];
              // console.log('%c⧭ currentSeccion', 'color: #00736b', currentSeccion.table.widths, currentSeccion.table.body);
            } else {
              seccionesContent.push({
                table: {
                  headerRows: 0,
                  widths: [220, '*'],
                  body: [
                    [{text: elem.categoria, alignment: 'center', bold: true, fontSize: 11, colSpan: 2}, {}],
                    [
                      {text: '', borderColor: [null, '#000']},
                      {text: '', borderColor: [null, '#000']},
                    ],
                  ],
                },
                margin: [0, 0, 0, 14],
                layout: {
                  hLineColor: function (i, node) {
                    return 'white';
                  },
                  hLineWidth: function (i, node) {
                    return 1;
                  },
                  vLineWidth: function (i) {
                    return 0;
                  },
                  paddingTop: function (i, node) {
                    return 0;
                  },
                  paddingBottom: function (i, node) {
                    return 5;
                  },
                },
              });
            }
            if (flags.na) {
              if (isDynamic && subsectionInfo?.currentRow) {
                subsectionInfo.currentRow.push({
                  text: i18n('common.notApplicable'),
                  colSpan: subsectionInfo.columnCount || 1,
                  alignment: 'center',
                  fontSize: dynamicFontSize,
                });
              } else if (!isDynamic) {
                const last = seccionesContent[seccionesContent.length - 1];
                last?.table?.body?.push([
                  {text: i18n('common.notApplicable'), alignment: 'center', colSpan: 2, fontSize: 10},
                  {},
                ]);
              }
            }
          } else if (tipo === 'pregunta') {
            if (elem?.isTaskExecutorFromSource) return;
            const dynamicMargin = [nPregunta > 9 ? 13 : 10, 0, 0, 0];
            // get current seccion content
            const currentSeccion = seccionesContent[seccionesContent.length - 1];
            const tituloPregunta = pregunta.titulo || pregunta.label;
            const rows: any[][] = [
              [
                {
                  text: [{text: `${nPregunta}.`, bold: true}, ` ${tituloPregunta}`],
                  fontSize: 9,
                  margin: [0, textMargin + 1, 0, 0],
                },
              ],
            ];
            if (elem?.allowNotApplicable && elem?.notApplicable) {
              rows[0].push(
                text(
                  i18n('common.notApplicable'),
                  subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                ),
              );
            } else switch (tipoRespuesta) {
              case 'files':
                // if (subsectionInfo) {
                //   subsectionInfo.currentRow.push({ text: nPregunta, alignment: 'right', fontSize: 7 });
                // }
                rows[0].push(
                  text(
                    `${files?.length ? files.map((f) => f.name).join(', ') : ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                if (images?.length) {
                  //rows[0][0].rowSpan = (rows[0][0].rowSpan || 1) + 1;
                  //rows[0][0].text += '\n  Imágenes: ';
                  //rows.push(['','', '']);
                  rows.push([text('Imágenes: ', {border: [true, false, false, false], margin: dynamicMargin}), {}]); // mejor una fila directamente por mientras
                  headerRows++;
                  (await imgsGrid(images, {margin: dynamicMargin})).forEach((img) => {
                    rows.push([{...img, colSpan: 2}, {}]);
                  });
                }
                break;
              case 'ammount':
                rows[0].push(
                  text(
                    `${ammount || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'day':
                const days = {
                  monday: 'Lunes',
                  tuesday: 'Martes',
                  wednesday: 'Miercoles',
                  thursday: 'Jueves',
                  friday: 'Viernes',
                  saturday: 'Sabado',
                  sunday: 'Domingo',
                };
                rows[0].push(
                  text(
                    `${days[day] || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'month':
                const months = {
                  january: 'Enero',
                  february: 'Febrero',
                  march: 'Marzo',
                  april: 'Abril',
                  may: 'Mayo',
                  june: 'Junio',
                  july: 'Julio',
                  august: 'Agosto',
                  september: 'Septiembre',
                  october: 'Octubre',
                  november: 'Noviembre',
                  december: 'Diciembre',
                };
                rows[0].push(
                  text(
                    `${months[month] || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'year':
                rows[0].push(
                  text(
                    `${year || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'meterReading':
                rows[0].push(
                  text(
                    `${meterReading || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'textField':
                rows[0].push(
                  text(
                    `${textField || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'barcode':
                rows[0].push(
                  text(
                    `${barcode || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'registrationNumber':
                rows[0].push(
                  text(
                    `${registrationNumber || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'textFromImage':
                rows[0].push(
                  text(
                    `${textFromImage || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'numberField':
                rows[0].push(
                  text(
                    `${numberField || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'checkbox':
                rows[0].push(
                  text(
                    `${
                      isIncidentYesNoKeyPointer(elem.keyPointer)
                        ? yesNo || (checkbox ? 'Si' : checkbox === false ? 'No' : '')
                        : checkbox
                          ? i18n('common.yes')
                          : i18n('common.no')
                    }`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'date':
                rows[0].push(
                  text(
                    `${date ? formatIncidentDateAnswerIfBlumar(date, 'DD-MM-YYYY', elem.keyPointer, tenant) : ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'signature':
                const signatureImage = await resolveProcedureSignatureImage(signature, signatureRef);
                if (signatureImage) rows[0].push({columns: [{image: signatureImage, fit: [100, 100]}], margin: replyMargin});
                else
                  rows[0].push(
                    text('', subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin}),
                  );
                break;
              case 'inspectionCheck':
                rows[0].push({
                  layout: (cell, row) => {
                    return {
                      paddingTop: function (i, node) {
                        return 0;
                      },
                      paddingBottom: function (i, node) {
                        return 0;
                      },
                    };
                  },
                  margin: [10, 0, 0, 2],
                  table: {
                    body: [
                      [
                        {text: 'Aprobado', fillColor: inspectionCheck === 'Aprobado' ? 'green' : 'white', fontSize: 8},
                        {text: 'Observado', fillColor: inspectionCheck === 'Observado' ? 'orange' : 'white', fontSize: 8},
                        {text: 'Rechazado', fillColor: inspectionCheck === 'Rechazado' ? 'red' : 'white', fontSize: 8},
                      ],
                    ],
                  },
                });
                break;
              case 'yesNo':
                rows[0].push(
                  text(
                    `${yesNo || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'time':
                rows[0].push(
                  text(
                    `${time ? formatProcedureTimeForPdf(time) : ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'location':
                let locations = location ? (Array.isArray(location) ? location : [location]) : undefined;
                rows[0].push(
                  text(
                    `${locations?.map((l) => l.label).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'perfilEmpleado':
                let perfilEmpleados = perfilEmpleado
                  ? Array.isArray(perfilEmpleado)
                    ? perfilEmpleado
                    : [perfilEmpleado]
                  : undefined;
                rows[0].push(
                  text(
                    `${perfilEmpleados?.map((l) => l.label).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'articulo':
                let articulos = articulo ? (Array.isArray(articulo) ? articulo : [articulo]) : undefined;
                rows[0].push(
                  text(
                    `${articulos?.map((l) => l.label).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'usuario':
                let usuarios = usuario ? (Array.isArray(usuario) ? usuario : [usuario]) : undefined;
                rows[0].push(
                  text(
                    `${usuarios?.map((l) => l.label).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'asignadorFuncion':
                let teams = asignadorFuncion
                  ? Array.isArray(asignadorFuncion)
                    ? asignadorFuncion
                    : [asignadorFuncion]
                  : undefined;
                rows[0].push(
                  text(
                    `${teams?.map((l) => l.label || l.nombreFuncion).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'thirdParty':
                let thirdParties = thirdParty ? (Array.isArray(thirdParty) ? thirdParty : [thirdParty]) : undefined;
                rows[0].push(
                  text(
                    `${thirdParties?.map((l) => l.label).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'project':
                let projects = project ? (Array.isArray(project) ? project : [project]) : undefined;
                rows[0].push(
                  text(
                    `${projects?.map((l) => l.label || l.name).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'asset':
                let assets = asset ? (Array.isArray(asset) ? asset : [asset]) : undefined;
                rows[0].push(
                  text(
                    `${assets?.map((l) => l.label).join(', ') || ''}`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              case 'multiCheckbox':
                const answerList: any = [];
                const showOnlySelected = pdfMultiCheckboxSelectedOnly || hasTreeAlternatives(alternativasCheckbox);
                if (multiCheckbox?.length) {
                  multiCheckbox?.forEach((a, i) => {
                    if ((showOnlySelected && a.value) || !showOnlySelected) {
                      const answerLabel = a.pathLabel || a.fullLabel || a.label;
                      const label =
                        a.value && answerLabel === 'Otros' && allowOthersOption && multiCheckboxOthers
                          ? `${answerLabel}: ${multiCheckboxOthers}`
                          : answerLabel;
                      answerList.push([
                        {},
                        {
                          columns: [
                            {
                              stack: [
                                {text: a.value ? '√' : ' ', margin: [0, 0, 0, -10]},
                                {
                                  canvas: [
                                    {
                                      type: 'polyline',
                                      lineWidth: 1,
                                      closePath: true,
                                      points: [
                                        {x: 0, y: 0},
                                        {x: 8, y: 0},
                                        {x: 8, y: 8},
                                        {x: 0, y: 8},
                                      ],
                                    },
                                  ],
                                },
                              ],
                              width: 5,
                              margin: [10, 1],
                            },
                            {text: label, fontSize: 8, margin: [0, 3, 0, 0]},
                          ],
                        },
                      ]);
                    }
                  });
                  if (answerList.length > 0) {
                    rows[0].push(answerList);
                  }

                  //rows.push([{text:'', margin:[0,-5,0,0], colSpan:2},{}]);
                } else {
                  rows[0].push(
                    text(
                      'No seleccionado',
                      subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                    ),
                  );
                }
                break;
              case 'multipleChoice': {
                const isOthersAnswer = String(multipleChoice) === 'others';
                let displayText = '';
                if (isOthersAnswer) {
                  displayText = `Otros: ${multipleChoiceOthers || ''}`;
                } else {
                  displayText =
                    getMultipleChoiceTreeDisplayLabel(
                      alternativas,
                      multipleChoice,
                      elem,
                      'pdf',
                      (list, val) => findOptionByValue(list, val),
                    ) ||
                    findOptionByValue(alternativas, multipleChoice) ||
                    '';
                }
                rows[0].push(
                  text(
                    displayText,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
              }
              default:
                rows[0].push(
                  text(
                    `Tipo respuesta <${tipoRespuesta}> no definido.`,
                    subsectionInfo ? {margin: dynamicReplyMargin, fontSize: dynamicFontSize} : {margin: replyMargin},
                  ),
                );
                break;
            }

            if (haveCriticality) {
              headerRows++;
              rows.push([
                text(`Criticidad:`, {width: 'auto', margin: dynamicMargin}),
                {
                  margin: [10, 0, 0, 0],
                  layout: (cell, row) => {
                    return {
                      paddingTop: function (i, node) {
                        return 0;
                      },
                      paddingBottom: function (i, node) {
                        return 0;
                      },
                    };
                  },
                  table: {
                    body: [
                      [
                        {text: 'Baja', fillColor: criticality === 'Baja' ? 'green' : 'white', fontSize: 8},
                        {text: 'Media', fillColor: criticality === 'Media' ? 'orange' : 'white', fontSize: 8},
                        {text: 'Alta', fillColor: criticality === 'Alta' ? 'red' : 'white', fontSize: 8},
                      ],
                    ],
                  },
                },
              ]);
            }
            if (comentario) {
              headerRows++;
              rows.push([
                text(`Comentarios adicionales: `, {margin: [nPregunta > 9 ? 13 : 10, 3, 0, 1]}),
                text(`${comentario}`, {margin: [10, 3, 0, 1]}),
              ]);
            }
            if (additionalPhotos?.length) {
              if (!subsectionInfo) {
                headerRows++;
                rows.push([text('Fotos adicionales: ', {margin: dynamicMargin}), {}]);
                (await imgsGrid(additionalPhotos, {margin: dynamicMargin, columnGap})).forEach((img) => {
                  rows.push([{...img, colSpan: 2}, {}]);
                });
              }
            }
            // Recolectar imágenes para mini tabla cuando la vista dinámica no las muestra (additionalPhotos + images tipo files)
            const allPhotos = [...(additionalPhotos || []), ...(images || [])].filter(Boolean);
            if (subsectionInfo && allPhotos.length > 0) {
              subsectionInfo.attachedImagesList = subsectionInfo.attachedImagesList || [];
              subsectionInfo.attachedImagesList.push({
                row: subsectionInfo.current,
                label: tituloPregunta,
                photos: allPhotos,
              });
            }
            //rows.push([{}, {}]);
            // rows.push([
            //   {text: '', borderColor: [null, '#E8E8E8']},
            //   {text: '', borderColor: [null, '#E8E8E8']},
            // ]);
            if (subsectionInfo) {
              subsectionInfo.currentRow.push(rows[0][1] || {text: ''});
            } else {
              if (currentSeccion?.table) {
                preguntaContent.table.headerRows = headerRows;
                preguntaContent.table.body.push(...rows);
                currentSeccion.table.body.push(
                  [preguntaContent, {}],
                  [
                    {text: '', borderColor: [null, '#E8E8E8']},
                    {text: '', borderColor: [null, '#E8E8E8']},
                  ],
                );
              }
            }
          }
        }),
      );
      if (pdfQualityScoreColumns) {
        const categoriasMayor = ['DESCOLORADAS', 'BLANDAS', 'FUERTE', 'PERDIDA PARIETAL'];

        const categoriasMenor = ['LEVEMENTE DESCOLORADAS', 'LEVEMENTE BLANDAS', 'LEVE OLOR', 'ATAQUE PROFUNDO'];

        const includeColumnsWith = ['OLOR', 'COLOR', 'TEXTURA', 'ATAQUE'];

        function contarCategorias(row: any, categorias: string[], columnLabels: any[]): number {
          let count: number = 0;
          columnLabels.forEach((label, index) => {
            const rowValue = row[index + 1];
            // console.log('%c⧭ rowValue', 'color: #ffcc00', index + 1, rowValue);
            const rowText = rowValue?.text;
            const hasKey = includeColumnsWith.find((key) => label.includes(key));
            if (hasKey || label.includes('PARÁSITOS')) {
              // tamaño especifico para que estas ocupen menos espacio?
              row[index + 1].fontSize = 6;
            }
            if (hasKey && categorias.includes(rowText)) {
              count++;
            }
          });
          return count;
        }
        const criterioAR = (total: number): string => (total >= 4 ? 'R' : 'A');

        // entries of subsectionMap
        for (const [key, value] of subsectionMap.entries()) {
          const {tableRef, columnasAdicionales, columnLabels} = value;
          // console.log('%c⧭ columnLabels', 'color: #ffcc00', columnLabels);
          const subsectionTableBody = tableRef.table.body;
          const bodyRows = subsectionTableBody.length;
          // console.log('%c⧭ subsectionTableBody', 'color: #ffcc00', subsectionTableBody);
          for (let i = 0; i < bodyRows; i++) {
            if (i > 1) {
              let row = subsectionTableBody[i];
              const rowCount = row.length;
              const columnCount = columnLabels.length;
              if (rowCount > columnCount) {
                // slice to remove the last columns
                row = row.slice(0, columnCount + 1);
                subsectionTableBody[i] = row;
              }
              const mayor = contarCategorias(row, categoriasMayor, columnLabels);
              const menor = contarCategorias(row, categoriasMenor, columnLabels);
              const total = mayor * 2 + menor; // La fórmula del total
              const ar = criterioAR(total);
              row.push(
                ...[mayor, menor, total, ar].map((text) => ({
                  text,
                  bold: true,
                  margin: dynamicReplyMargin,
                  fontSize: dynamicFontSize,
                  alignment: 'right',
                })),
              );
            }
          }
        }
        // console.log('%c⧭ seccionesContent', 'color: #ffcc00', seccionesContent);
      }
      // Insertar grilla de imágenes adjuntas debajo de cada tabla dinámica
      for (const [, subsectionInfo] of subsectionMap.entries()) {
        if (subsectionInfo?.attachedImagesList?.length) {
          const cells: {photo: any; caption: string}[] = [];
          for (const item of subsectionInfo.attachedImagesList) {
            for (const photo of item.photos || []) {
              if (photo) {
                cells.push({
                  photo,
                  caption: `Fila ${item.row} - ${item.label || ''}`,
                });
              }
            }
          }
          if (cells.length > 0) {
            const photosPerRow = 4;
            const imgBases = await Promise.all(cells.map((c) => urlToBase64(c.photo?.publicUrl || c.photo, {resize: true})));
            const stacks = cells.map((cell, i) => ({
              stack: [
                {image: imgBases[i], fit: [180, 180]},
                {text: cell.caption, fontSize: 7, alignment: 'center', margin: [0, 2, 0, 0]},
              ],
              width: 'auto',
            }));
            const gridRows = breakArray(stacks, photosPerRow).map((row) => ({
              columns: row,
              margin: [0, 4],
            }));
            const idx = seccionesContent.indexOf(subsectionInfo.tableRef);
            if (idx !== -1) {
              seccionesContent.splice(idx + 1, 0, ...gridRows);
            }
          }
        }
      }
      content.push(...seccionesContent);
    }
    if (horizontalTable) {
      const answersBySection: any = {};
      const imagesBySection: any = {};
      const sectionOrder: string[] = [];
      let currentSection = '';

      const horizontalNa = createAncestorNaTracker();
      listaProcedimiento.forEach((part, partIndex) => {
        horizontalNa.observe(part);
        if (part.tipo === 'seccion') {
          currentSection = part.categoria || '';
          if (!answersBySection[currentSection]) {
            answersBySection[currentSection] = {};
            imagesBySection[currentSection] = imagesBySection[currentSection] || [];
            sectionOrder.push(currentSection);
          }
          return;
        }

        if (part.tipo === 'pregunta') {
          if (horizontalNa.covered) return;
          if (part?.isTaskExecutorFromSource) return;
          const dynamicSubsection = findDynamicSubsectionHeaderForItem(listaProcedimiento, partIndex);
          if (!dynamicSubsection) {
            return;
          }
          // Recolectar imágenes para mini tabla (la tabla horizontal no las muestra)
          const allPhotos = [...(part.additionalPhotos || []), ...(part.images || [])].filter(Boolean);
          if (allPhotos.length > 0 && currentSection) {
            const tituloPregunta = part.pregunta?.titulo || part.pregunta?.label || '';
            if (!imagesBySection[currentSection]) imagesBySection[currentSection] = [];
            imagesBySection[currentSection].push({label: tituloPregunta, photos: allPhotos});
          }

          tipoRespuestaOptions.forEach((tipoRespuesta) => {
            if (
              part.tipoRespuesta === tipoRespuesta.value &&
              !answersBySection[currentSection]?.[part.pregunta?.titulo || part.pregunta?.label || '']?.some(
                (t) => t.value === tipoRespuesta.value && t.questionIndex === partIndex,
              )
            ) {
              const tituloPregunta = part.pregunta?.titulo || part.pregunta?.label || '';
              const answerType: any = {
                ...tipoRespuesta,
                nPregunta: part.nPregunta,
                questionIndex: partIndex,
                questionTitle: tituloPregunta,
                answer: null,
              };

              let taskAnswer = part?.[answerType.value];

              if (part?.allowNotApplicable && part?.notApplicable) {
                taskAnswer = i18n('common.notApplicable');
              } else if (answerType.value === 'date') {
                taskAnswer = taskAnswer ? moment(taskAnswer).format('DD/MM/YYYY') : '';
              } else if (answerType.value === 'time') {
                taskAnswer = taskAnswer ? formatProcedureTimeForPdf(taskAnswer) : '';
              } else if (answerType.value === 'checkbox') {
                taskAnswer = taskAnswer ? i18n('common.yes') : i18n('common.no');
              } else if (answerType.value === 'multiCheckbox') {
                taskAnswer = taskAnswer
                  ?.filter((a) => a?.value)
                  ?.map((a) => a.pathLabel || a.fullLabel || a.label)
                  ?.join(', ');
                if (part?.multiCheckboxOthers) {
                  taskAnswer += `: ${part.multiCheckboxOthers}`;
                }
              } else if (answerType.value === 'multipleChoice') {
                if (taskAnswer === 'others') {
                  taskAnswer = 'Otros: ' + (part?.multipleChoiceOthers || '');
                } else {
                  taskAnswer =
                    getMultipleChoiceTreeDisplayLabel(
                      part?.alternativas,
                      taskAnswer,
                      part,
                      'pdf',
                      (list, val) => findOptionByValue(list, val),
                    ) ||
                    findOptionByValue(part?.alternativas, taskAnswer) ||
                    taskAnswer ||
                    '';
                }
              } else if (Array.isArray(taskAnswer)) {
                taskAnswer = taskAnswer
                  ?.filter(Boolean)
                  ?.map((a) => a.label || a.toString())
                  ?.join(', ');
              } else if (taskAnswer?.label) {
                taskAnswer = taskAnswer.label;
              }

              // temporal para camanchaca calidad
              if (isCamanchacaCalidad && tituloPregunta.includes('Final') && !tituloPregunta.includes('A/R') && !taskAnswer) {
                taskAnswer = '0';
              }
              if (isCamanchacaCalidad && tituloPregunta.includes('Final') && tituloPregunta.includes('A/R') && !taskAnswer) {
                taskAnswer = 'Aprobado';
              }

              answerType.answer = taskAnswer;

              if (!answersBySection[currentSection]) {
                answersBySection[currentSection] = {};
                sectionOrder.push(currentSection);
              }
              if (!answersBySection[currentSection][tituloPregunta]) {
                answersBySection[currentSection][tituloPregunta] = [];
              }
              answersBySection[currentSection][tituloPregunta].push(answerType);
            }
          });
        }
      });

      for (const sectionKey of sectionOrder) {
        const groupedAnswerTypes = answersBySection[sectionKey];
        const maxLength = Math.max(...Object.values(groupedAnswerTypes).map((arr: any) => arr.length));
        const maxVirtualLength = pageOrientation === 'landscape' ? 20 : 10;
        const pdfMakeMaxLength = Math.min(maxVirtualLength, maxLength);
        const groupKeys = Object.keys(groupedAnswerTypes);

        const body: any = [];
        if (pdfMakeMaxLength > 0) {
          const columnNumbers = [
            {text: '', bold: true, fontSize: 8, borderColor: [null, null]},
            ...Array(pdfMakeMaxLength)
              .fill(0)
              .map((_, i) => ({
                text: String(i + 1),
                alignment: 'center',
                bold: true,
                fontSize: 9,
                borderColor: [null, null],
              })),
          ];

          body.push(columnNumbers);
        }

        groupKeys.forEach((elem) => {
          const answers = groupedAnswerTypes[elem] || [];
          const totalChunks = Math.ceil(answers.length / pdfMakeMaxLength);

          for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const chunkStart = chunkIndex * pdfMakeMaxLength;

            const headerCell = {
              text: chunkIndex === 0 ? elem : '',
              alignment: 'left',
              bold: true,
              fontSize: 7,
              borderColor: [null, null],
            };

            const answerCells = Array(pdfMakeMaxLength)
              .fill('')
              .map((_, i) => {
                const answer = answers[chunkStart + i];
                // first split by comma or space and then substring each part to 10 characters, then rejoin
                let answerText = answer?.answer
                  ? answer.answer
                      .toString()
                      .split(' ')
                      .map((part) => part.trim().substring(0, 8))
                      .join(' ')
                  : '';
                // if (answerText) {
                //   answerText = answerText
                //     .split(',')
                //     .map((part) => part.trim().substring(0, 10))
                //     .join(', ');
                // }
                const answerIsNumeric = isNumeric(answerText);
                const alignment = answerIsNumeric ? 'right' : 'left';

                return {
                  text: answerText,
                  alignment: alignment,
                  fontSize: answerIsNumeric ? 6.5 : 6,
                  borderColor: [null, null],
                  margin: [0, 0],
                };
              });

            body.push([headerCell, ...answerCells]);
          }
        });

        if (body.length > 0) {
          content.push({text: sectionKey, bold: true, alignment: 'center', margin: [0, 10, 0, 4]});
          content.push({
            margin: [0, -5, 0, 14],
            layout: {
              hLineColor: () => '#E8E8E8',
              hLineWidth: () => 1,
              vLineWidth: () => 0,
              paddingTop: () => 4,
              paddingBottom: () => 3,
              paddingLeft: () => 0,
              paddingRight: () => 0,
            },
            table: {
              headerRows: 1,
              dontBreakRows: false,
              keepWithHeaderRows: 1,
              widths: ['auto', ...Array(pdfMakeMaxLength).fill('*')],
              body,
            },
          });
          // Grilla de imágenes adjuntas (la tabla horizontal no las muestra)
          const sectionImages = imagesBySection[sectionKey] || [];
          if (sectionImages.length > 0) {
            const cells: {photo: any; caption: string}[] = [];
            for (const item of sectionImages) {
              for (const photo of item.photos || []) {
                if (photo) {
                  cells.push({photo, caption: item.label || ''});
                }
              }
            }
            if (cells.length > 0) {
              const photosPerRow = 4;
              const imgBases = await Promise.all(cells.map((c) => urlToBase64(c.photo?.publicUrl || c.photo, {resize: true})));
              const stacks = cells.map((cell, i) => ({
                stack: [
                  {image: imgBases[i], fit: [120, 120]},
                  {text: cell.caption, fontSize: 7, alignment: 'center', margin: [0, 2, 0, 0]},
                ],
                width: 'auto',
              }));
              const gridRows = breakArray(stacks, photosPerRow).map((row) => ({
                columns: row,
                margin: [0, 4],
              }));
              content.push(...gridRows);
            }
          }
        }
      }
    }

    // console.log('%c⧭ content', 'color: #ffcc00', content);

    //detalles generales
    const detallesGeneralesBody: any = [];
    if (tipoVisualizacion === 'Estandar') {
      //if (comentarioEnviado)
      detallesGeneralesBody.push([
        {
          text: 'Detalles Generales',
          bold: true,
          fontSize: 10,
          alignment: 'center',
          colSpan: 2,
        },
        {},
      ]);
      detallesGeneralesBody.push([
        {text: 'Comentario', bold: true, fontSize: 10, alignment: 'left'},
        {text: comentarioEnviado ? comentarioEnviado : 'Sin comentarios', fontSize: 10, alignment: 'left'},
      ]);
    }
    //if (comentarioEnviado) content.push(text('Detalles Generales: ' + comentarioEnviado));
    if (documentosEnviados?.length || tipoVisualizacion === 'Estandar') {
      detallesGeneralesBody.push([
        {text: 'Documentos', bold: true, fontSize: 10, alignment: 'left'},
        {
          text: documentosEnviados?.length > 0 ? documentosEnviados.map((d) => d.name).join(', ') : 'Sin documentos',
          fontSize: 10,
          alignment: 'left',
        },
      ]);
    }
    if (fotosEnviadas?.length || tipoVisualizacion === 'Estandar') {
      detallesGeneralesBody.push([
        {
          text: 'Imágenes',
          bold: true,
          fontSize: 10,
          alignment: 'left',
          border: [true, true, !(fotosEnviadas?.length > 0), !(fotosEnviadas?.length > 0)],
        },
        {
          text: fotosEnviadas?.length > 0 ? '' : i18n('common.withoutImages'),
          fontSize: 10,
          alignment: 'left',
          border: [!(fotosEnviadas?.length > 0), true, true, !(fotosEnviadas?.length > 0)],
        },
      ]);
      if (fotosEnviadas?.length) {
        const imgsRow: any = [];
        (await imgsGrid(fotosEnviadas, {margin: [0, 5]}, imgsPerRow, {fit: imgFit})).forEach((img) => {
          imgsRow.push([{...img, colSpan: 2}, {}]);
        });
        detallesGeneralesBody.push([
          {
            stack: imgsRow,
            colSpan: 2,
            border: [true, false, true, true],
          },
          {},
        ]);
      }
    }
    if (detallesGeneralesBody?.length) {
      content.push(
        infoTable(
          {
            widths: [80, '*'],
            body: detallesGeneralesBody,
          },
          {margin: [0, 25]},
        ),
      );
    }
    //fin detalles generales

    if (firmas?.length) {
      const columns: any = [];
      for (const firma of firmas) {
        const signatureSource = firma.firma || firma?.firmaRef?.downloadUrl || firma?.firmaRef?.publicUrl;
        if (!signatureSource) {
          // columns.push(text('Firma de ' + firma.responsable?.fullName || 'Nombre no registrado.', {fontSize: 9}));
          // columns.push(text('Firma no encontrada', {fontSize: 9}));
          continue;
        }
        const firmaImage = await resolveProcedureSignatureImage(firma.firma, firma?.firmaRef);
        const stack = [
          {
            layout: {
              defaultBorder: false,
              paddingLeft: function (i, node) {
                return 0;
              },
              paddingRight: function (i, node) {
                return 0;
              },
              paddingTop: function (i, node) {
                return 0;
              },
              paddingBottom: function (i, node) {
                return 0;
              },
              hLineColor: function (i, node) {
                return '#E8E8E8';
              },
              vLineColor: function (i, node) {
                return '#E8E8E8';
              },
              hLineWidth: function (i, node) {
                return 0.3;
              },
              vLineWidth: function (i) {
                return 0.3;
              },
            },
            table: {
              widths: ['*', 'auto', '*'],
              body: [[{}, {image: firmaImage, fit: [200, 200], border: [true, true, true, true]}, {}]],
            },
          },
          {text: firma.responsable?.fullName || 'Nombre no registrado', fontSize: 9},
          {text: firma.responsable?.perfilEmpleado?.puesto?.nombre || '', fontSize: 9},
          {text: firma.fechaFirmada ? moment(firma.fechaFirmada).format('DD-MM-YYYY') : '', fontSize: 7},
        ];
        columns.push({
          stack,
          margin: 1,
          alignment: 'center',
        });
      }
      //console.log('%c⧭ columns', 'color: #aa0041', columns);
      if (columns.length) {
        const tablaFirma = {
          layout: 'table',
          width: '*',
          table: {
            headerRows: 1,
            dontBreakRows: true,
            keepWithHeaderRows: 1,
            body: [
              [{text: 'Firmas de Validación ', bold: true, fontSize: 10, alignment: 'center'}] /*Firma de validación */,
              ...breakArray(columns, 2).map((columns) => [
                {columns, margin: [0, 25, 0, 25], border: [false, false, false, false]},
              ]),
            ],
            widths: ['*'],
          },
          margin: [0, 10],
          // layout: {
          //   vLineWidth: function (i) {
          //     return 0;
          //   },
          //   hLineWidth: function (i) {
          //     return 0;
          //   },
          // },
        };
        content.push(tablaFirma);
      }
    }

    if (imagenesTarea?.length > 0) {
      const imagesFit = pageOrientation === 'landscape' ? [390, 250] : [250, 350];
      const imagenesBody = await imgsGrid(imagenesTarea, {margin: [0, 10]}, 2, {fit: imagesFit, resize: false, margin: [0, 0]});
      content.push({margin: [0, 0], pageBreak: 'before', stack: imagenesBody});
    }

    return {
      content: content.map((c, index) => ({...c, id: tarea.id + '_' + index})),
      // taskHeader: tituloTable,
      id: tarea.id,
    };
  },
  exportDataAsPdf: async (tarea, {tenant = null, settings = null, open = true, pageOrientation = 'portrait'}: any = {}) => {
    const {isConsolidated, titulo, tituloTarea, selectedTasks = []} = tarea;
    const taskId = tarea.id;

    if (
      USE_BACKEND_FOR_PDF &&
      !isConsolidated &&
      (!selectedTasks || selectedTasks.length === 0) &&
      taskId &&
      taskId !== 'consolidatedTask'
    ) {
      try {
        const {base64, filename} = await TareaService.downloadPdf(taskId, null, {
          pageOrientation,
        });
        if (!base64 || !filename) {
          throw new Error('Respuesta inválida del backend');
        }
        const byteCharacters = atob(base64.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {type: 'application/pdf'});
        if (open) {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 100);
          return null;
        } else {
          return {
            getDataUrl: async () => base64,
            getBlob: async () => blob,
          };
        }
      } catch (err) {
        console.warn('[tareaPdf] Fallback a frontend tras error del backend:', err);
      }
    }

    const tituloDocumento = `${getTaskVisualLabel(tarea) || titulo} ${tituloTarea}`; // (${estado})
    tenant = tenant || (await TenantService.findCurrent());
    settings = settings || (await SettingsService.find());
    let logo: any = null;
    if (settings?.logosPdf?.length) {
      logo = await urlToBase64(settings.logosPdf[0].publicUrl);
    } else if (settings?.logos?.length) {
      logo = await urlToBase64(settings.logos[0].publicUrl);
    }
    const tenantName = tenant.name ?? '';
    const tituloTable = (currentTask) => {
      const {titulo, tituloTarea, subtitulo, isConsolidated, comienzo, fin} = currentTask;
      const tituloPdf = `${getTaskVisualLabel(currentTask) || titulo} ${tituloTarea}`; // (${estado})
      const fechaIni = comienzo ? moment(comienzo).format('DD-MM-YYYY HH:mm') : null;
      const fechaFin = fin ? moment(fin).format('DD-MM-YYYY HH:mm') : null;
      return {
        layout: 'headerTable',
        width: '*',
        alignment: 'center',
        table: {
          widths: ['auto', '*', 'auto'],
          body: [
            [
              logo
                ? {minWidth: 80, maxWidth: 100, image: logo, maxHeight: 40, rowSpan: 2, margin: [0, 5]}
                : {text: tenantName, rowSpan: 2, margin: [0, 5]},
              {
                text: [{text: tituloPdf, fontSize: 13, bold: true}, subtitulo ? {text: '\n' + subtitulo, fontSize: 10} : {}],
                margin: [8, 5],
                rowSpan: 2,
              },
              fechaIni
                ? {
                    text: [
                      {text: isConsolidated ? i18n('common.start') : i18n('common.start'), bold: true, noWrap: true, fontSize: 9},
                      {text: '\n'},
                      {text: fechaIni || '', noWrap: true, fontSize: 10},
                    ],
                    margin: [1, 3],
                    alignment: 'left',
                  }
                : {},
            ],
            [
              {},
              {},
              fechaFin
                ? {
                    text: [
                      {text: isConsolidated ? i18n('common.end') : i18n('common.end'), bold: true, noWrap: true, fontSize: 9},
                      {text: '\n'},
                      {text: fechaFin || '', noWrap: true, fontSize: 10},
                    ],
                    margin: [1, 3],
                    alignment: 'left',
                  }
                : {},
              //fechaVencimiento ? { text: [{text: 'Vencimiento: ', fontSize: 10, bold: true}, fechaVencimiento], alignment: 'right' } : ''
            ],
          ],
        },
      };
    };
    if (isConsolidated) {
      tarea.id = 'consolidatedTask';
    }
    let breakPoints: any = [];
    const tareas = [tarea, ...selectedTasks];
    const pdfFeatureKeys = getTenantPdfFeatureKeysFromStore();
    const contents = await Promise.all(
      tareas.map((t) => tareaPdf.generatePdfContent(t, {tenant, settings, pageOrientation, featureKeys: pdfFeatureKeys})),
    );
    // flat content but put a {text: '', pageBreak: 'before'} between each content
    const flatContent = contents.reduce((acc, content, index) => {
      if (index > 0) {
        // const currentHeader = content.taskHeader;
        acc.push({text: '', pageBreak: index > 0 ? 'before' : undefined});
      }
      return acc.concat(content.content);
    }, [] as any);
    /**
     * DEBUG/SAFE HELPERS
     * - detecta tablas descuadradas (row.length !== widths.length)
     * - normaliza filas (rellena o recorta) para evitar calcWidth undefined
     */
    function normalizeTableNode(node: any) {
      if (!node?.table?.widths || !node?.table?.body) return;

      const widthsLen = node.table.widths.length;

      node.table.body = node.table.body.map((row: any, i: number) => {
        const r = Array.isArray(row) ? [...row] : [];
        while (r.length < widthsLen) r.push({text: ''});
        if (r.length > widthsLen) r.length = widthsLen;
        return r;
      });
    }

    function walkAndValidateTables(node: any, path = 'root', issues: any[] = []) {
      if (!node) return issues;

      if (Array.isArray(node)) {
        node.forEach((n, i) => walkAndValidateTables(n, `${path}[${i}]`, issues));
        return issues;
      }

      // tabla
      if (node.table?.widths && node.table?.body) {
        const w = node.table.widths.length;
        node.table.body.forEach((row: any, i: number) => {
          const len = Array.isArray(row) ? row.length : -1;
          if (len !== w) {
            issues.push({
              path,
              rowIndex: i,
              widths: w,
              rowLen: len,
              sampleRow: row,
            });
          }
        });
      }

      // recorrer hijos típicos de pdfmake
      if (node.stack) walkAndValidateTables(node.stack, `${path}.stack`, issues);
      if (node.columns) walkAndValidateTables(node.columns, `${path}.columns`, issues);
      if (node.content) walkAndValidateTables(node.content, `${path}.content`, issues);

      // otros nodos frecuentes
      if (node.ul) walkAndValidateTables(node.ul, `${path}.ul`, issues);
      if (node.ol) walkAndValidateTables(node.ol, `${path}.ol`, issues);

      return issues;
    }

    function walkAndNormalizeTables(node: any) {
      if (!node) return;

      if (Array.isArray(node)) {
        node.forEach((n) => walkAndNormalizeTables(n));
        return;
      }

      if (node.table?.widths && node.table?.body) {
        normalizeTableNode(node);
      }

      if (node.stack) walkAndNormalizeTables(node.stack);
      if (node.columns) walkAndNormalizeTables(node.columns);
      if (node.content) walkAndNormalizeTables(node.content);
      if (node.ul) walkAndNormalizeTables(node.ul);
      if (node.ol) walkAndNormalizeTables(node.ol);
    }
    let dd: any = () => ({
      content: cloneDeep(flatContent),
      info: {
        title: tituloDocumento,
        author: 'planiwise.com',
      },
      styles: {
        tableExample: {
          margin: [-20, 5, 5, 0],
        },
        firmaValidacion: {
          margin: [0, 0, 0, 0],
        },
      },
      defaultStyle: {
        alignment: 'left',
        fontSize: 11,
        bold: false,
        columnGap: 20,
      },
      // a string or { width: number, height: number }
      pageSize: 'A4',
      // by default we use portrait, you can change it to landscape if you wish
      pageOrientation: pageOrientation,
      // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
      pageMargins: [30, 100, 30, 50],
      header: function (currentPage) {
        // Loop through the breakpoints and set custom header based on task id
        let tarea: any = null;
        for (var i = 0; i < breakPoints.length; i++) {
          if (breakPoints[i].page === currentPage) {
            tarea = tareas.find((t) => t.id === breakPoints[i].id);
          }
        }
        let hd: any = {
          margin: [30, 20, 30, 40],
          columns: [],
        };
        if (tarea) {
          // console.log('%c⧭ header page', 'color: #ff6600', currentPage, tarea);
          hd.columns.push(tituloTable(tarea));
        }
        return [hd];
      },
      footer: function (currentPage, pageCount) {
        const footerText = `Página ${currentPage} de ${pageCount} `;
        const byStemwaste = 'Generado con planiwise.com';
        let fd: any = {
          margin: [30, 20, 30, 10],
          columns: [
            {text: footerText, alignment: 'left', color: '#303030', fontSize: 8},
            {text: ''},
            {text: byStemwaste, alignment: 'right', color: '#A0A0A0', fontSize: 8},
          ],
        };
        return [fd];
      },
    });
    // await new Promise<void>((resolve) => {
    //   pdfMake.createPdf(dd).download(tituloPdf, () => {
    //     resolve();
    //   });
    // });
    //console.log('%c⧭ dd', 'color: #aa0041', dd);
    pdfMake.addTableLayouts({
      table: {
        hLineWidth: function (i, node) {
          return 0.5;
        },
        vLineWidth: function (i) {
          return 0.5;
        },
        hLineColor: function (i) {
          return '#080808';
        },
        vLineColor: function (i) {
          return '#080808';
        },
      },
      headerTable: {
        hLineWidth: function (i, node) {
          return 0.5;
        },
        vLineWidth: function (i) {
          return 0.5;
        },
        hLineColor: function (i) {
          return '#080808';
        },
        vLineColor: function (i) {
          return '#080808';
        },
        paddingTop: (rowIndex, node) => {
          if (rowIndex > 0) return 0;
          const rowNode = node.table.body[rowIndex];
          rowNode.forEach((cellNode, cellIndex) => {
            /* Hack para alinear verticalmente al centro en esta libreria */
            if (cellIndex > 1) return 0;
            const _calcWidth = node.table.widths[1]?._calcWidth;
            if (!_calcWidth) return 0;
            const maxWidth = _calcWidth;
            const text = cellNode._inlines;
            const image = !!cellNode.image;
            let itemHeight: any,
              margin: any,
              cell: any = null;
            if (image) {
              itemHeight = cellNode._height;
              margin = (66 - itemHeight) / 2;
              cell = cellNode;
              //console.log('%c⧭ image', 'color: #994d75', {rowIndex, cellIndex, image, cell, margin, itemHeight});
            }
            if (text) {
              let usedWidth = 0;
              let maxHeight = 0;
              let lastHadLineEnd = false;
              text.forEach((inline, i) => {
                // console.log('%c⧭ inline', 'color: #994d75', {
                //   inline,
                //   usedWidth,
                //   maxWidth,
                //   lastHadLineEnd,
                //   itemHeight,
                //   maxHeight,
                // });
                usedWidth += inline.width;
                if (usedWidth > maxWidth || lastHadLineEnd) {
                  maxHeight += inline.height;
                  usedWidth = inline.width;
                } else {
                  maxHeight = Math.max(inline.height, maxHeight);
                }
                lastHadLineEnd = !!inline.lineEnd;
              });

              itemHeight = maxHeight;

              margin = (66 - itemHeight) / 2;
              cell = cellNode;

              // console.log('%c⧭ text', 'color: #994d75', {
              //   rowIndex,
              //   cellIndex,
              //   text,
              //   cell,
              //   margin,
              //   itemHeight,
              // });
            }
            // set cell margin
            if (cell) {
              if (cell._margin) {
                cell._margin[1] = margin;
              } else {
                cell._margin = [0, margin, 0, 0];
              }
            }
          });
          return 0;
        },
      },
    });
    const definitionForDebug = dd();

    // 1) Validar tablas antes de renderizar
    const issues = walkAndValidateTables(definitionForDebug.content);
    if (issues.length) {
      console.log('🚨 PDFMAKE: Se detectaron tablas descuadradas (rowLen != widths). Total:', issues.length);
      // Log detallado (limitado para no explotar la consola)
      issues.slice(0, 25).forEach((it, idx) => {
        console.log(`❌ [${idx + 1}]`, {
          path: it.path,
          rowIndex: it.rowIndex,
          widths: it.widths,
          rowLen: it.rowLen,
          sampleRow: it.sampleRow,
        });
      });

      // 2) Normalizar tablas para evitar crash
      console.log('🧯 Normalizando tablas automáticamente para evitar calcWidth...');
      walkAndNormalizeTables(definitionForDebug.content);

      // Re-validar para confirmar que quedó OK
      const issuesAfter = walkAndValidateTables(definitionForDebug.content);
      console.log('✅ Re-validación luego de normalizar. Issues restantes:', issuesAfter.length);
    }

    // 3) Log para debug
    //console.log('%c⧭ PDF export [definitionForDebug]', 'color: #cc0036', definitionForDebug);

    // Generate the PDF to get the positions of content and populate the breakPoints array
    var pdfDoc = pdfMake.createPdf(definitionForDebug);

    await pdfDoc.getBuffer();

    var content = definitionForDebug.content;
    // Iterate through content and record page numbers for each task id
    for (var i = 0; i < content.length; i++) {
      const positions = content[i].positions;
      if (content[i].id && positions) {
        positions.forEach((p) => {
          var top = p.top;
          var page = p.pageNumber;
          // for each time top fits in 631 add 1 to page (841 - 210 which is the height of the header)
          while (top > 631) {
            top -= 631;
            page++;
          }
          if (!breakPoints.some((b) => b.page === page)) {
            const taskId = content[i].id.split('_')[0];
            breakPoints.push({page: page, id: taskId});
          }
        });
      }
    }
    //console.log('%c⧭', 'color: #cc0036', {breakPoints, content});

    // Para el PDF final, usa también una definición ya validada/normalizada
    const definitionFinal = dd();
    const finalIssues = walkAndValidateTables(definitionFinal.content);
    if (finalIssues.length) {
      walkAndNormalizeTables(definitionFinal.content);
    }

    const pdf = pdfMake.createPdf(definitionFinal);
    if (open) {
      pdf.open();
    }
    return pdf;
  },
};
export default tareaPdf;
